import {
  TourCardHeadless,
  TourCloseHeadless,
  TourOverlayHeadless,
} from '@tour-kit/react/headless';
import { Button, cn } from '@up2stay/up2promo-ui';
import { X } from 'lucide-react';
import type { CSSProperties, KeyboardEvent, ReactNode } from 'react';
import { useEffect, useRef } from 'react';

import { useDictionary } from '@/core/providers/dictionary-provider';

function StepMeter({
  current,
  total,
  className,
}: {
  current: number;
  total: number;
  className?: string;
}) {
  return (
    <div className={cn('flex items-center gap-1', className)} aria-hidden>
      {Array.from({ length: total }, (_, index) => {
        const filled = index <= current;
        return (
          <span
            key={index}
            className={cn(
              'h-1 flex-1 rounded-full transition-[background-color,transform] duration-200 ease-out',
              'motion-reduce:transition-none',
              filled ? 'scale-y-100 bg-foreground' : 'scale-y-90 bg-border',
            )}
          />
        );
      })}
    </div>
  );
}

const FOCUSABLE =
  'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Keyboard frame for the tour card (DEV-540): focus moves into the card on
 * every step, Tab cycles inside it, and Escape skips the tour — the overlay
 * hides the page, so focus must not stay on it.
 */
function TourCardFocusFrame({
  stepKey,
  onEscape,
  setFloating,
  style,
  className,
  children,
}: {
  stepKey: string;
  onEscape: () => void;
  setFloating: (node: HTMLElement | null) => void;
  style: CSSProperties;
  className: string;
  children: ReactNode;
}) {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const onEscapeRef = useRef(onEscape);
  useEffect(() => {
    onEscapeRef.current = onEscape;
  });

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    const primary =
      card.querySelector<HTMLElement>('[data-tour-primary]') ?? card;
    primary.focus({ preventScroll: true });
  }, [stepKey]);

  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      onEscapeRef.current();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const trapTab = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') return;
    const focusables = Array.from(
      cardRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [],
    );
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div
      ref={(node) => {
        cardRef.current = node;
        setFloating(node);
      }}
      style={style}
      className={className}
      role="dialog"
      aria-modal="true"
      aria-labelledby="orientation-tour-title"
      tabIndex={-1}
      onKeyDown={trapTab}
    >
      {children}
    </div>
  );
}

/** The welcome host is a 1×1 px anchor; real targets are larger. */
function isCentredStepAnchor(rect: DOMRect): boolean {
  return rect.width <= 2 && rect.height <= 2;
}

/**
 * Status Board tour chrome — ink primary, tonal card, headless TourKit positioning.
 */
export function OrientationTourChrome() {
  const dict = useDictionary();
  const nav = dict.guidance.tours.orientation.nav;

  return (
    <>
      <TourOverlayHeadless
        render={({ overlayStyle, cutoutStyle, targetRect, interactive }) => (
          <div
            className="orientation-tour-overlay z-[9998] overflow-hidden"
            style={overlayStyle}
            aria-hidden="true"
          >
            {targetRect && isCentredStepAnchor(targetRect) ? (
              // Centred steps anchor on the 1px welcome host: a cutout there
              // showed as a small grey dot mid-screen (DEV-574). Dim fully.
              <div className="orientation-tour-scrim absolute inset-0" />
            ) : targetRect ? (
              <div
                className="orientation-tour-cutout"
                style={{
                  ...cutoutStyle,
                  pointerEvents: interactive ? 'auto' : 'none',
                }}
              />
            ) : null}
          </div>
        )}
      />
      <TourCardHeadless
        render={({
          isActive,
          currentStep,
          currentStepIndex,
          totalSteps,
          isFirstStep,
          isLastStep,
          next,
          prev,
          skip,
          floatingStyles,
          refs,
        }) => {
          if (!isActive || !currentStep) return null;

          const isWelcome = currentStep.id === 'welcome';
          const title =
            typeof currentStep.title === 'string'
              ? currentStep.title
              : dict.guidance.tours.orientation.steps.welcome.title;

          return (
            <TourCardFocusFrame
              stepKey={currentStep.id}
              onEscape={skip}
              setFloating={refs.setFloating}
              style={floatingStyles}
              className={cn(
                'orientation-tour-card z-[9999] outline-none',
                'w-[min(22.5rem,calc(100vw-1.5rem))]',
                'rounded-[var(--radius-surface,1rem)] border border-border/80',
                'bg-card/95 text-card-foreground shadow-md backdrop-blur-md',
                'motion-safe:animate-in motion-safe:fade-in-0 motion-safe:zoom-in-95 motion-safe:duration-200',
                isWelcome &&
                  'orientation-tour-card--welcome w-[min(26rem,calc(100vw-1.5rem))]',
              )}
            >
              <div className="flex items-start justify-between gap-3 px-4 pt-4">
                <div className="min-w-0 flex-1 space-y-2">
                  {isWelcome ? (
                    <p className="text-[0.6875rem] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                      {dict.guidance.tours.orientation.kicker}
                    </p>
                  ) : (
                    <StepMeter
                      current={currentStepIndex}
                      total={totalSteps}
                      className="max-w-[10rem]"
                    />
                  )}
                  <h2
                    id="orientation-tour-title"
                    className="text-balance text-base font-semibold tracking-tight"
                  >
                    {title}
                  </h2>
                </div>
                <TourCloseHeadless
                  aria-label={nav.close}
                  className={cn(
                    'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
                    'text-muted-foreground transition-colors hover:bg-accent hover:text-foreground',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                  )}
                >
                  <X className="h-4 w-4" aria-hidden />
                </TourCloseHeadless>
              </div>

              <div className="px-4 pb-1 pt-2 text-sm text-muted-foreground">
                {currentStep.content}
              </div>

              <div className="flex items-center justify-between gap-2 px-4 pb-4 pt-3">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground"
                  onClick={() => {
                    skip();
                  }}
                >
                  {nav.skip}
                </Button>
                <div className="flex items-center gap-2">
                  {!isFirstStep ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        prev();
                      }}
                    >
                      {nav.back}
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    data-tour-primary
                    onClick={() => {
                      next();
                    }}
                  >
                    {isWelcome
                      ? nav.startTour
                      : isLastStep
                        ? nav.done
                        : nav.next}
                  </Button>
                </div>
              </div>
            </TourCardFocusFrame>
          );
        }}
      />
    </>
  );
}
